import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Create Rx — the same prescription API bridge's /prescription/{patientId}
 * screen uses (see APIConfig.prescription). Everything is keyed on PATIENT id.
 *
 * The one call that writes is save(): `status:false` stores a draft,
 * `status:true` publishes. Publishing also asks the backend to notify the
 * patient — those switches are QUERY params, not body fields.
 */
class PrescriptionService {
  constructor() {
    this.apiService = new APIService();
    // Side effect: installs String.prototype.format for the URL templates.
    this.helper = new Helper();
  }

  
  /** The latest PUBLISHED prescription (bridge's "View Rx"). */
  fetchLatest(patientId, onResponse, onError) {
    const url = APIConfig.prescription.latest.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The patient's measured vitals, to prefill the Patient-details card:
   * { PersonalDetails: { height, heightUOM, weight, bmi,
   *   day/month/yearWeightRecorded } }. Only that half is used —
   * `dateOfWeightRecorded` is REQUIRED by the save and has no other source
   * until phr has its own vitals endpoint, which this then becomes.
   *
   * NOTE: the response's `Reports` half is deliberately IGNORED. phr's clinical
   * cards come from phr's own APIs, not bridge's tracking service (Nittin,
   * 2026-07-27) — chief complaints uses CC v2 today; diagnosis, reports,
   * medicines and investigations attach when their phr endpoints exist.
   */
  getPersonalDetails(patientId, employeeId, employeeName, onResponse, onError) {
    const url = APIConfig.prescription.personalDetails.format(
      patientId,
      employeeId,
      encodeURIComponent(employeeName || "")
    );

    return this.apiService.get(url, onResponse, onError);
  }

  /* ---------- the write ---------- */

  /**
   * Save a draft (`body.status === false`) or publish (`true`).
   *
   * `notify` = { email, sms, whatsapp, phone, clientCode } — the patient-
   * notification switches, which the API takes as QUERY params. They only apply
   * on a publish; a draft save passes them empty, exactly like bridge.
   *
   * An existing draft (body.id) goes to PUT /update, a fresh one to
   * POST /create — and create uses postAgain, NEVER post: post() re-fires on any
   * non-2xx, and a re-fired publish would send the patient a second prescription.
   */
  save(body, notify, onResponse, onError) {
    const n = notify || {};
    const query =
      `?email=${encodeURIComponent(n.email || "")}` +
      `&clientCode=${encodeURIComponent(n.clientCode || "")}` +
      `&sms=${n.sms ? "true" : "false"}` +
      `&phone=${encodeURIComponent(n.phone || "")}` +
      `&whatsapp=${n.whatsapp ? "true" : "false"}`;

    if (body.id) {
      return this.apiService.put(
        APIConfig.prescription.update + query,
        body,
        onResponse,
        onError
      );
    }
    return this.apiService.postAgain(
      APIConfig.prescription.create + query,
      body,
      onResponse,
      onError
    );
  }
}

export default PrescriptionService;
